// openEHR AM model — BMM package org.openehr.am.aom2.archetype
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { ARCHETYPE_ONTOLOGY, ASSERTION } from "../_unassigned.ts";
import type { C_COMPLEX_OBJECT } from "./constraint_model.ts";
import type { ARCHETYPE_TERMINOLOGY } from "./terminology.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Archetype equivalent to ARCHETYPED class in Common reference model. Defines semantics of identfication, lifecycle, versioning, composition and specialisation.
 */
export class ARCHETYPE extends openehr_base.AUTHORED_RESOURCE {
  /**
   * Root node of the definition of this archetype.
   */
  definition?: C_COMPLEX_OBJECT;
  /**
   * The ontology of the archetype.
   */
  ontology?: ARCHETYPE_ONTOLOGY;
  /**
   * Internal storage for adl_version
   * @protected
   */
  protected _adl_version?: openehr_base.String;

  /**
   * ADL version if archetype was read in from an ADL sharable archetype.
   */
  get adl_version(): string | undefined {
    return this._adl_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for adl_version.
   * Use this to access openehr_base.String methods.
   */
  get $adl_version(): openehr_base.String | undefined {
    return this._adl_version;
  }

  /**
   * Sets adl_version from either a primitive value or openehr_base.String wrapper.
   */
  set adl_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._adl_version = undefined;
    } else if (typeof val === "string") {
      this._adl_version = openehr_base.String.from(val);
    } else {
      this._adl_version = val;
    }
  }

  /**
   * Multi-axial identifier of this archetype in archetype space.
   */
  archetype_id?: openehr_base.ARCHETYPE_ID;
  /**
   * Archetype OID (HIER_OBJECT_ID). Distinct from AUTHORED_RESOURCE.uid (UUID) in the BASE model.
   */
  archetype_hier_uid?: openehr_base.HIER_OBJECT_ID;
  /**
   * Internal storage for concept
   * @protected
   */
  protected _concept?: openehr_base.String;

  /**
   * The normative meaning of the archetype as a whole, expressed as a local archetype code, typically “at0000”.
   */
  get concept(): string | undefined {
    return this._concept?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for concept.
   * Use this to access openehr_base.String methods.
   */
  get $concept(): openehr_base.String | undefined {
    return this._concept;
  }

  /**
   * Sets concept from either a primitive value or openehr_base.String wrapper.
   */
  set concept(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._concept = undefined;
    } else if (typeof val === "string") {
      this._concept = openehr_base.String.from(val);
    } else {
      this._concept = val;
    }
  }

  /**
   * Identifier of the specialisation parent of this archetype.
   */
  parent_archetype_id?: openehr_base.ARCHETYPE_ID;
  /**
   * Invariant statements about this object. Statements are expressed in first order predicate logic, and usually refer to at least two attributes.
   */
  invariants?: ASSERTION[];
  /**
   * The concept name of the archetype in language a_lang; corresponds to the term definition of the concept attribute in the archetype ontology.
   * @param a_lang - Parameter
   * @returns Result value
   */
  concept_name(a_lang: openehr_base.String): openehr_base.String {
    // TODO: Implement concept_name behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method concept_name not yet implemented.");
  }

  /**
   * Set of language-independent paths extracted from archetype. Paths obey Xpath-like syntax and are formed from alternations of C_OBJECT.node_id and C_ATTRIBUTE.rm_attribute_name values.
   * @returns Result value
   */
  physical_paths(): openehr_base.String {
    // TODO: Implement physical_paths behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method physical_paths not yet implemented.");
  }

  /**
   * Set of language-dependent paths extracted from archetype. Paths obey the same syntax as physical_paths, but with node_ids replaced by their meanings from the ontology.
   * @param lang - Parameter
   * @returns Result value
   */
  logical_paths(lang: openehr_base.String): openehr_base.String {
    // TODO: Implement logical_paths behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method logical_paths not yet implemented.");
  }

  /**
   * Specialisation depth of this archetype; larger than 0 if this archetype has a parent. Derived from terminology.specialisation_depth.
   * @returns Result value
   */
  specialisation_depth(): openehr_base.Integer {
    // TODO: Implement specialisation_depth behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method specialisation_depth not yet implemented.");
  }

  /**
   * True if this archetype is a specialisation of another.
   * @returns Result value
   */
  is_specialised(): openehr_base.Boolean {
    // TODO: Implement is_specialised behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_specialised not yet implemented.");
  }

  /**
   * True if the archetype is valid overall; various tests should be used, including checks on node_ids, internal references, and constraint references.
   * @returns Result value
   */
  is_valid(): openehr_base.Boolean {
    // TODO: Implement is_valid behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_valid not yet implemented.");
  }

  /**
   * True if every node_id found on a C_OBJECT node is found in ontology.term_codes.
   * @returns Result value
   */
  node_ids_valid(): openehr_base.Boolean {
    // TODO: Implement node_ids_valid behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method node_ids_valid not yet implemented.");
  }

  /**
   * Version of predecessor archetype of this archetype, if any.
   * @returns Result value
   */
  previous_version(): openehr_base.String {
    // TODO: Implement previous_version behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method previous_version not yet implemented.");
  }

  /**
   * True if every ARCHETYPE_INTERNAL_REF. target_path refers to a legitimate node in the archetype definition.
   * @returns Result value
   */
  internal_references_valid(): openehr_base.Boolean {
    // TODO: Implement internal_references_valid behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method internal_references_valid not yet implemented.");
  }

  /**
   * True if every CONSTRAINT_REF.reference found on a C_OBJECT node in the archetype definition is found in ontology.constraint_codes.
   * @returns Result value
   */
  constraint_references_valid(): openehr_base.Boolean {
    // TODO: Implement constraint_references_valid behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method constraint_references_valid not yet implemented.");
  }

  /**
   * The short concept name of the archetype extracted from the archetype_id.
   * @returns Result value
   */
  short_concept_name(): openehr_base.String {
    // TODO: Implement short_concept_name behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method short_concept_name not yet implemented.");
  }

  version(): openehr_base.String {
    // TODO: Implement version behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method version not yet implemented.");
  }
}

/**
 * Root object of a standalone, authored archetype, including all meta-data, description, other identifiers and lifecycle.
 */
export class AUTHORED_ARCHETYPE extends ARCHETYPE {
  /**
   * ADL version if archetype was read in from an ADL sharable archetype.
   */
  override get adl_version(): string | undefined {
    return this._adl_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for adl_version.
   * Use this to access openehr_base.String methods.
   */
  override get $adl_version(): openehr_base.String | undefined {
    return this._adl_version;
  }

  /**
   * Sets adl_version from either a primitive value or openehr_base.String wrapper.
   */
  override set adl_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._adl_version = undefined;
    } else if (typeof val === "string") {
      this._adl_version = openehr_base.String.from(val);
    } else {
      this._adl_version = val;
    }
  }

  /**
   * Unique identifier of this archetype artefact instance. A new identifier is assigned every time the content is changed by a tool. Used by tools to distinguish different revisions and/or interim snapshots of the same artefact.
   */
  build_uid?: openehr_base.UUID;
  /**
   * Internal storage for rm_release
   * @protected
   */
  protected _rm_release?: openehr_base.String;

  /**
   * Semver.org compatible release of the most recent reference model release on which the archetype in its current version is based. This does not imply conformance only to this release, since an archetype may be valid with respect to multiple releases of a reference model.
   */
  get rm_release(): string | undefined {
    return this._rm_release?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_release.
   * Use this to access openehr_base.String methods.
   */
  get $rm_release(): openehr_base.String | undefined {
    return this._rm_release;
  }

  /**
   * Sets rm_release from either a primitive value or openehr_base.String wrapper.
   */
  set rm_release(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_release = undefined;
    } else if (typeof val === "string") {
      this._rm_release = openehr_base.String.from(val);
    } else {
      this._rm_release = val;
    }
  }

  /**
   * Internal storage for is_generated
   * @protected
   */
  protected _is_generated?: openehr_base.Boolean;

  /**
   * If True, indicates that this artefact was machine-generated from some other source, in which case, tools would expect to overwrite this artefact on a new generation. Editing tools should set this value to False when a user starts to manually edit an archetype.
   */
  get is_generated(): boolean | undefined {
    return this._is_generated?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_generated.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_generated(): openehr_base.Boolean | undefined {
    return this._is_generated;
  }

  /**
   * Sets is_generated from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_generated(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_generated = undefined;
    } else if (typeof val === "boolean") {
      this._is_generated = openehr_base.Boolean.from(val);
    } else {
      this._is_generated = val;
    }
  }

  other_meta_data?: undefined;
}

/**
 * Class representing source template, i.e. a kind of archetype that may include template overlays, and may be restricted by tools to only defining mandations, prohibitions, and restrictions on elements already defined in the flat parent.
 */
export class TEMPLATE extends AUTHORED_ARCHETYPE {
  /**
   * Overlay archetypes, i.e. partial archetypes that include full definition and terminology, but logically derive all their meta-data from the owning template.
   */
  overlays?: undefined;
  /**
   * Better / ADL 1.4 template identifier (`ChemoForm-MBA.v8`).
   * Several templates may specialise one `archetype_id`; this id keeps them apart.
   */
  template_id?: string;
}

/**
 * Root object of an operational template. An operational template is derived from a \`TEMPLATE\` definition and the \`ARCHETYPEs\` and/or \`TEMPLATE_OVERLAYs\` mentioned by that template by a process of flattening, and potentially removal of unneeded languages and terminologies.
 *
 * An operational template is used for generating and validating RM-canonical instance data, and also as a source artefact for generating other downstream technical artefacts, including XML schemas, APIs and UI form definitions.
 */
export class OPERATIONAL_TEMPLATE extends AUTHORED_ARCHETYPE {
  /**
   * Better / ADL 1.4 template identifier copied from the source template during
   * flatten (`ChemoForm-MBA.v8`). Web Template `templateId` and OPT XML
   * `<template_id>` prefer this over `archetype_id` when it is set.
   */
  template_id?: string;
  /**
   * Compendium of flattened terminologies of archetypes  referenced from this template, keyed by archetype identifier. This will almost always be present in a template.
   */
  component_terminologies?: undefined;
  /**
   * Compendium of flattened terminology extracts (i.e. from external terminologies) from archetypes referenced from this template, keyed by archetype identifier.
   */
  terminology_extracts?: undefined;
  component_terminology(an_id: openehr_base.String): ARCHETYPE_TERMINOLOGY {
    // TODO: Implement component_terminology behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method component_terminology not yet implemented.");
  }
}

/**
 * A concrete form of the bare \`ARCHETYPE\` class, used to represent overlays in a source template. Overlays have no meta-data of their own, and are instead documented by their owning template.
 */
export class TEMPLATE_OVERLAY extends ARCHETYPE {
}

/**
 * Human-readable structured identifier (HRID) for an archetype or template.
 */
export class ARCHETYPE_HRID {
  /**
   * Internal storage for namespace
   * @protected
   */
  protected _namespace?: openehr_base.String;

  /**
   * Reverse domain name namespace identifier.
   */
  get namespace(): string | undefined {
    return this._namespace?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for namespace.
   * Use this to access openehr_base.String methods.
   */
  get $namespace(): openehr_base.String | undefined {
    return this._namespace;
  }

  /**
   * Sets namespace from either a primitive value or openehr_base.String wrapper.
   */
  set namespace(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._namespace = undefined;
    } else if (typeof val === "string") {
      this._namespace = openehr_base.String.from(val);
    } else {
      this._namespace = val;
    }
  }

  /**
   * Internal storage for rm_publisher
   * @protected
   */
  protected _rm_publisher?: openehr_base.String;

  /**
   * Name of the Reference Model publisher.
   */
  get rm_publisher(): string | undefined {
    return this._rm_publisher?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_publisher.
   * Use this to access openehr_base.String methods.
   */
  get $rm_publisher(): openehr_base.String | undefined {
    return this._rm_publisher;
  }

  /**
   * Sets rm_publisher from either a primitive value or openehr_base.String wrapper.
   */
  set rm_publisher(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_publisher = undefined;
    } else if (typeof val === "string") {
      this._rm_publisher = openehr_base.String.from(val);
    } else {
      this._rm_publisher = val;
    }
  }

  /**
   * Internal storage for rm_package
   * @protected
   */
  protected _rm_package?: openehr_base.String;

  /**
   * Name of the package in whose reachability graph the \`_rm_class_\` class is found (there can be more than one possibility in many reference models).
   */
  get rm_package(): string | undefined {
    return this._rm_package?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_package.
   * Use this to access openehr_base.String methods.
   */
  get $rm_package(): openehr_base.String | undefined {
    return this._rm_package;
  }

  /**
   * Sets rm_package from either a primitive value or openehr_base.String wrapper.
   */
  set rm_package(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_package = undefined;
    } else if (typeof val === "string") {
      this._rm_package = openehr_base.String.from(val);
    } else {
      this._rm_package = val;
    }
  }

  /**
   * Internal storage for rm_class
   * @protected
   */
  protected _rm_class?: openehr_base.String;

  /**
   * Name of the root class of this archetype.
   */
  get rm_class(): string | undefined {
    return this._rm_class?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_class.
   * Use this to access openehr_base.String methods.
   */
  get $rm_class(): openehr_base.String | undefined {
    return this._rm_class;
  }

  /**
   * Sets rm_class from either a primitive value or openehr_base.String wrapper.
   */
  set rm_class(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_class = undefined;
    } else if (typeof val === "string") {
      this._rm_class = openehr_base.String.from(val);
    } else {
      this._rm_class = val;
    }
  }

  /**
   * Internal storage for concept_id
   * @protected
   */
  protected _concept_id?: openehr_base.String;

  /**
   * The short concept name of the archetype as used in the multi-axial \`_archetype_hrid_\`.
   */
  get concept_id(): string | undefined {
    return this._concept_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for concept_id.
   * Use this to access openehr_base.String methods.
   */
  get $concept_id(): openehr_base.String | undefined {
    return this._concept_id;
  }

  /**
   * Sets concept_id from either a primitive value or openehr_base.String wrapper.
   */
  set concept_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._concept_id = undefined;
    } else if (typeof val === "string") {
      this._concept_id = openehr_base.String.from(val);
    } else {
      this._concept_id = val;
    }
  }

  /**
   * Internal storage for release_version
   * @protected
   */
  protected _release_version?: openehr_base.String;

  /**
   * The full numeric version of this archetype consisting of 3 parts, e.g. \`"1.8.2"\`. The \`_archetype_hrid_\` feature includes only the major version.
   */
  get release_version(): string | undefined {
    return this._release_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for release_version.
   * Use this to access openehr_base.String methods.
   */
  get $release_version(): openehr_base.String | undefined {
    return this._release_version;
  }

  /**
   * Sets release_version from either a primitive value or openehr_base.String wrapper.
   */
  set release_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._release_version = undefined;
    } else if (typeof val === "string") {
      this._release_version = openehr_base.String.from(val);
    } else {
      this._release_version = val;
    }
  }

  /**
   * The status of the version, i.e.:
   *
   * * released: (empty string)
   * * release_candidate: \`"rc"\`
   * * alpha: \`"alpha"\`
   * * beta: \`"beta"\`
   */
  version_status?: openehr_base.VERSION_STATUS;
  /**
   * Internal storage for build_count
   * @protected
   */
  protected _build_count?: openehr_base.String;

  /**
   * The build count since last increment of any version part.
   */
  get build_count(): string | undefined {
    return this._build_count?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for build_count.
   * Use this to access openehr_base.String methods.
   */
  get $build_count(): openehr_base.String | undefined {
    return this._build_count;
  }

  /**
   * Sets build_count from either a primitive value or openehr_base.String wrapper.
   */
  set build_count(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._build_count = undefined;
    } else if (typeof val === "string") {
      this._build_count = openehr_base.String.from(val);
    } else {
      this._build_count = val;
    }
  }

  /**
   * The 'interface' form of the HRID, i.e. down to the major version.
   * @returns Result value
   */
  semantic_id(): openehr_base.String {
    // TODO: Implement semantic_id behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method semantic_id not yet implemented.");
  }

  /**
   * The 'physical' form of the HRID, i.e. with complete version information specified by \`_version_id()_\`.
   * @returns Result value
   */
  physical_id(): openehr_base.String {
    // TODO: Implement physical_id behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method physical_id not yet implemented.");
  }

  /**
   * Full version identifier string, based on \`_release_version_\`, \`_version_status_\`, and \`_build_count_\` e.g. \`"1.8.2-rc.4"\`.
   * @returns Result value
   */
  version_id(): openehr_base.String {
    // TODO: Implement version_id behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method version_id not yet implemented.");
  }

  /**
   * Major version of this archetype, extracted from \`_release_version_\`.
   * @returns Result value
   */
  major_version(): openehr_base.String {
    // TODO: Implement major_version behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method major_version not yet implemented.");
  }

  /**
   * Minor version of this archetype, extracted from \`_release_version_\`.
   * @returns Result value
   */
  minor_version(): openehr_base.String {
    // TODO: Implement minor_version behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method minor_version not yet implemented.");
  }

  /**
   * Patch version of this archetype, extracted from \`_release_version_\`. Equivalent to patch version in patch version in \`semver.org\` sytem.
   * @returns Result value
   */
  patch_version(): openehr_base.String {
    // TODO: Implement patch_version behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method patch_version not yet implemented.");
  }
}
